import React, { useEffect, useState } from 'react';
import { Box, Container } from '@mui/material';
import { motion } from 'framer-motion';
import PageBanner from '../../../components/PageBanner';
import AdmissionForm from './components/AdmissionForm';
import AdmissionEligibility from './components/AdmissionEligibility';
import AdmissionRefundPolicy from './components/AdmissionRefundPolicy';
import AdmissionFAQ from './components/AdmissionFAQ';
import AdmissionSidebar from './components/AdmissionSidebar';
import AdmissionSupport from './components/AdmissionSupport';
import admissionDefault from '../../../data/admission/admission.json';
import { getAdmissionPage } from '../../../services/data.service';
import { mediaUrl } from '../../../services/assetUrl';

const AdmissionProcessMain = () => {
    // Scroll animation variants
    const fadeInUp = {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    // Backend-driven content with bundled JSON as the instant fallback (same
    // pattern as Contact / Careers). Each section receives its slice as a prop.
    const [content, setContent] = useState(admissionDefault);
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await getAdmissionPage();
                const d = res?.data?.data; // SERVICE + wrapped doc → res.data.data
                if (active && d) setContent(d);
            } catch (e) {
                /* keep bundled fallback on 404 / error */
            }
        })();
        return () => { active = false; };
    }, []);

    const banner = content.banner || {};

    return (
        <Box sx={{ background: '#F8FAFC', pb: 10 }}>
            {/* HERO BANNER */}
            <PageBanner
                className="page-banner--tall"
                title={banner.title}
                eyebrow={banner.eyebrow}
                subtitle={banner.subtitle}
                facts={banner.facts || []}
                image={banner.bannerImage ? mediaUrl(banner.bannerImage) : undefined}
            />

            {/* MAIN CONTENT AREA */}
            <Container maxWidth="xl" sx={{ mt: { xs: 6, md: 10 }, position: 'relative', zIndex: 10, mb: 8, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: { xs: 5, md: 8 }, pr: { md: '78px' } }}>

                {/* Submit Your Query (form) + Regional Counselors — side by side, equal height */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start' }}>
                    <Box sx={{ width: { xs: '100%', md: '62%' }, display: 'flex' }}>
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-50px" }}
                            variants={fadeInUp}
                            style={{ width: '100%', display: 'flex' }}
                        >
                            <AdmissionForm data={content.form} />
                        </motion.div>
                    </Box>
                    <Box sx={{ width: { xs: '100%', md: '38%' }, display: 'flex' }}>
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-50px" }}
                            variants={fadeInUp}
                            style={{ width: '100%', display: 'flex' }}
                        >
                            <AdmissionSidebar data={content.sidebar} />
                        </motion.div>
                    </Box>
                </Box>

                {/* ROW 1: Eligibility (Left) / Support (Right) */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 6 }}>

                    {/* LEFT COLUMN: 70% */}
                    <Box sx={{ width: { xs: '100%', md: '70%' } }}>
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-50px" }}
                            variants={fadeInUp}
                        >
                            <AdmissionEligibility data={content.eligibility} />
                        </motion.div>
                    </Box>

                    {/* RIGHT COLUMN: 30% */}
                    <Box sx={{ width: { xs: '100%', md: '30%' } }}>
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-50px" }}
                            variants={fadeInUp}
                        >
                            <AdmissionSupport data={content.support} />
                        </motion.div>
                    </Box>

                </Box>

                {/* Refund Policy (full width) */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    variants={fadeInUp}
                >
                    <AdmissionRefundPolicy data={content.refund} />
                </motion.div>

                {/* 100% Width FAQ Section Below the Columns */}
                <Box sx={{ width: '100%' }}>
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                        variants={fadeInUp}
                    >
                        <AdmissionFAQ data={content.faq} />
                    </motion.div>
                </Box>
            </Container>

        </Box>
    );
};

export default AdmissionProcessMain;